import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import * as TimelineBookingsService from "_services/timeline-bookings-service";
import * as TimelineService from "_services/timeline-service";
import * as BookingService from "_services/booking-service";
import { createArrivalDoc } from '_utils/functions';
import { paxCheckOut, setBookingLocation, arrivalPaxCheckIn, deleteTimelineJob, unRejectPax } from './timeline-data';
import { updateBooking } from '../prm-bookings/bookings';
import { selectFlightsByBookingKey } from './timeline-flight-list';

const CLIENT_DUPLICATE_SUFFIX = "-duplicate-";

const stripClientDuplicateSuffix = (id) => {
    if (!id || typeof id !== "string") return id;
    const duplicateIndex = id.indexOf(CLIENT_DUPLICATE_SUFFIX);
    return duplicateIndex === -1 ? id : id.slice(0, duplicateIndex);
};

const normalizeClientDuplicateFlightKey = (booking, requestedFlightKey, baseBookingId) => {
    const isBoarding =
        requestedFlightKey?.endsWith("_BOARDING") ||
        booking?.isBoardingBooking ||
        booking?.combinedBooking ||
        booking?.originalBoardingBookingId;

    if (!isBoarding) return requestedFlightKey;

    const boardingKey = stripClientDuplicateSuffix(
        baseBookingId ||
        booking?.originalBoardingBookingId ||
        booking?._id ||
        booking?.id
    );

    return boardingKey?.includes("_BOARDING") ? boardingKey : requestedFlightKey;
};


const updateBookingInState = (state, updatedBooking, { mergeAll = false } = {}) => {

    if (typeof updatedBooking === "string") {
        const bookingId = updatedBooking;
        if (!bookingId) return;

        // iterate all flights because after delete we may not know the original flight key
        for (const key in state.bookingsByFlight) {
            const arr = state.bookingsByFlight[key];
            const idx = arr.findIndex(
                b =>
                    b._id === bookingId ||
                    b.id === bookingId ||
                    b?.originalBookingDoc?._id === bookingId
            );

            if (idx !== -1) {
                arr.splice(idx, 1);

                break;
            }
        }

        return; // stop here for delete
    }

    if (!updatedBooking || !updatedBooking._id) return;

    // Get flight confirmation details to build the key
    const fc = updatedBooking.flightConfirmationDetails || updatedBooking.flightDetails;
    if (!fc) return;

    const key = `${fc.carrierCode}${fc.flightNumber}_${fc.origin}_${fc.destination}_${fc.departureDate}`;

    // Find and update the booking in the list
    if (state.bookingsByFlight[key]) {

        const bookingIndex = state.bookingsByFlight[key].findIndex(
            b => b._id === updatedBooking._id || b.id === updatedBooking._id || b?.originalBookingDoc?._id === updatedBooking._id
        );

        // if booking index is found, update the booking
        if (bookingIndex !== -1) {

            // if mergeAll is true, update the entire booking
            if (mergeAll) {

                // returning iata parameter in the arrival pax check in response
                if (updatedBooking?.iata) {

                    // check type of booking
                    if (updatedBooking?.flightConfirmationDetails?.origin != updatedBooking?.iata) {
                        // arrival booking
                        state.bookingsByFlight[key][bookingIndex] = {
                            ...state.bookingsByFlight[key][bookingIndex],
                            originalBookingDoc: {
                                ...updatedBooking,
                            }
                        };
                    }
                    else {

                        // departure booking
                        state.bookingsByFlight[key][bookingIndex] = {
                            ...state.bookingsByFlight[key][bookingIndex],
                            ...updatedBooking,
                        };
                    }
                }
                else {

                    // Merge all properties from updated booking
                    state.bookingsByFlight[key][bookingIndex] = {
                        ...state.bookingsByFlight[key][bookingIndex],
                        ...updatedBooking,
                    };
                }
            }
            else {
                // Only update paxPositionInfo and paxPositionHistory
                state.bookingsByFlight[key][bookingIndex] = {
                    ...state.bookingsByFlight[key][bookingIndex],
                    paxPositionInfo: updatedBooking.paxPositionInfo,
                    paxPositionHistory: updatedBooking.paxPositionHistory,
                };
            }
        }
    }
};

/**
 * Get timeline bookings
 * Returns bookings data organized by flight
 */
export const getBookings = createAsyncThunk(
    "timelineBookings/getBookings",
    async ({ iata, startDate, endDate, rampBookings = false, airline = '', boardingBookings = false, jobsType = 'all' }) => {
        return TimelineBookingsService.getTimelineBookings({
            iata,
            startDate,
            endDate,
            rampBookings,
            airline,
            boardingBookings,
            jobsType
        });
    }
);

// post bookingsList
export const postBookingList = createAsyncThunk('bookings/postBookingList', async (params) => {
    return TimelineService.postBookings(params);
});

// add flight passenger (dedicated for timeline-v2 flight-information component)
export const addFlightPassenger = createAsyncThunk('bookings/addFlightPassenger', async (params) => {
    return TimelineService.addFlightPassenger(params);
});

// duplicate task
export const duplicateTask = createAsyncThunk('timelineBookings/duplicateTask', async (params) => {
    return TimelineBookingsService.duplicateTask(params);
});

/**
 * Update a booking for timeline-v2
 * This is a dedicated thunk for timeline-bookings slice (instead of relying on cross-slice action listening)
 * @param {Object} booking - { id: bookingId, body: { ...fieldsToUpdate } }
 */
export const updateTimelineBooking = createAsyncThunk(
    'timelineBookings/updateTimelineBooking',
    async (booking) => {
        return BookingService.updateBooking(booking);
    }
);

/**
 * Update an ALT pickup task (time, locations, assistance, notes, editHistory)
 * This is for updating task documents (not bookings) - specifically for ALT pickup tasks
 * @param {Object} params - { id, pickUpTime, pickUpLocation, dropOffLocation, passengerInformation, notes, editHistory }
 */
export const updateALTPickupTime = createAsyncThunk(
    'timelineBookings/updateALTPickupTime',
    async (params) => {
        const updatePayload = {
            id: params.id,
        };

        // Only include fields that are provided
        if (params.pickUpTime) {
            updatePayload.pickUpTime = params.pickUpTime;
        }
        if (params.pickUpLocation !== undefined) {
            updatePayload.pickUpLocation = params.pickUpLocation;
        }
        if (params.dropOffLocation !== undefined) {
            updatePayload.dropOffLocation = params.dropOffLocation;
        }
        if (params.passengerInformation !== undefined) {
            updatePayload.passengerInformation = params.passengerInformation;
        }
        if (params.notes !== undefined) {
            updatePayload.notes = params.notes;
        }
        if (params.editHistory !== undefined) {
            updatePayload.editHistory = params.editHistory;
        }

        return TimelineService.updateTask(updatePayload);
    }
);

export const manualUpdateBookingsTimes = createAsyncThunk(
    'timelineBookings/manualUpdateBookingsTimes',
    async (params) => {
        return TimelineService.manualUpdateBookingsTimesService(params);
    }
);

export const deleteBookingNote = createAsyncThunk(
    'timelineBookings/deleteBookingNote',
    async (params) => {
        return TimelineBookingsService.deleteBookingNote(params);
    }
);

const timelineBookingsSlice = createSlice({
    name: 'timelineBookings',
    initialState: {
        bookingsByFlight: {}, // Flight-based dictionary: { "EI101": [booking1, booking2], "BA825": [...] }
        status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'

        // post
        postStatus: 'idle',
        postBookings: {},
        postError: null,

        // add flight passenger (dedicated for timeline-v2 flight-information)
        addFlightPassengerStatus: 'idle',
        addFlightPassengerBookings: [],
        addFlightPassengerError: null,
        addFlightPassengerNeedsDepartureDate: false, // true when 422 is returned
        addFlightPassengerPendingBooking: null, // booking that needs departure date

        // task id's
        taskIds: [],
        rampFlightIds: [],
        rampMarshallFlightIds: [],

        // duplicate task
        duplicateStatus: 'idle',
        duplicatedTask: null,
        duplicateError: null,

        // update booking (for timeline-v2)
        updateStatus: 'idle',
        updateError: null,

        // update ALT pickup time
        updateALTPickupStatus: 'idle',
        updateALTPickupError: null,

        error: null,
        queryTime: null, // The last time we fetched data

        // Client-side duplicates — persisted separately so they survive bookingsByFlight refreshes
        clientDuplicates: [], // Array of { booking, flightKey }

        // manual update bookings times
        manualUpdateBookingsTimesStatus: 'idle',
        manualUpdateBookingsTimesError: null,

        // delete booking note
        deleteBookingNoteStatus: 'idle',
        deleteBookingNoteError: null,
    },
    reducers: {
        reset: (state) => {
            state.bookingsByFlight = {};
            state.status = 'idle';
            state.error = null;
            state.queryTime = null;
            state.clientDuplicates = [];
        },
        setQueryTime: (state, action) => {
            state.queryTime = action.payload;
        },
        setTaskIds: (state, action) => {
            state.taskIds = action.payload;
        },
        setRampFlightIds: (state, action) => {
            state.rampFlightIds = action.payload;
        },
        setRampMarshallFlightIds: (state, action) => {
            state.rampMarshallFlightIds = action.payload;
        },
        resetPostBookings: (state) => {
            state.postStatus = 'idle';
            state.postBookings = {};
            state.postError = null;
        },
        resetAddFlightPassenger: (state) => {
            state.addFlightPassengerStatus = 'idle';
            state.addFlightPassengerBookings = [];
            state.addFlightPassengerError = null;
            state.addFlightPassengerNeedsDepartureDate = false;
            state.addFlightPassengerPendingBooking = null;
        },
        setAddFlightPassengerNeedsDepartureDate: (state, action) => {
            state.addFlightPassengerNeedsDepartureDate = action.payload.needsDepartureDate;
            state.addFlightPassengerPendingBooking = action.payload.booking || null;
        },
        resetDuplicateTask: (state) => {
            state.duplicateStatus = 'idle';
            state.duplicatedTask = null;
            state.duplicateError = null;
        },
        resetUpdateBooking: (state) => {
            state.updateStatus = 'idle';
            state.updateError = null;
        },
        resetUpdateALTPickup: (state) => {
            state.updateALTPickupStatus = 'idle';
            state.updateALTPickupError = null;
        },
        removeClientDuplicate: (state, action) => {
            // Remove a client-side duplicate by ID (e.g. when it gets assigned or deleted)
            const idToRemove = action.payload;
            state.clientDuplicates = state.clientDuplicates.filter(
                dup => dup.booking._id !== idToRemove
            );
        },
        deleteClientDuplicate: (state, action) => {
            // Fully remove a client-side duplicate from both bookingsByFlight and clientDuplicates
            const idToRemove = action.payload;

            // Remove from clientDuplicates persistence
            state.clientDuplicates = state.clientDuplicates.filter(
                dup => dup.booking._id !== idToRemove
            );

            // Remove from bookingsByFlight
            for (const key in state.bookingsByFlight) {
                const arr = state.bookingsByFlight[key];
                const idx = arr.findIndex(b => b._id === idToRemove);
                if (idx !== -1) {
                    arr.splice(idx, 1);
                    break;
                }
            }
        },
        duplicateBookingLocally: (state, action) => {
            // Client-side duplication for ramp/boarding bookings
            const rawBooking = action.payload?.booking;
            if (!rawBooking) return;
            const flightKey = action.payload?.flightKey;
            const assignedDuplicateIds = action.payload?.assignedDuplicateIds || [];
            if (!flightKey) return;

            // Strip task-specific fields that shouldn't carry over to a new unassigned duplicate
            // These are added during task assignment / worker actions and don't belong on a clean booking
            const {
                // Assignment fields
                employeeId, assignedTime, allocatedTime,
                selectedTimeline, dropOffLocation, pickUpLocation,
                // Task lifecycle flags
                active, completed, acknowledged, stopped,
                cancelled, pending, notPlanned, requireUpdates,
                // Task timing
                startTime, completeTime, acknowledgedTime, checkInTime,
                // Boarding task fields
                boardingStarted, boardingStartTime,
                // Location tracking fields
                isAtLocation, atLocationTime, atLocationInformation,
                atLocationGeofenceVerified, atLocationQrVerified, atLocationBeaconVerified,
                // Passenger position tracking
                paxPositionInfo,
                // Task-specific reformatted fields (keep flightInformation & flightId so duplicates retain flight context)
                passengerInformation,
                taskNotes,
                ...cleanBooking
            } = { ...rawBooking };

            // Use the original booking ID as the base — when duplicating from a duplicate,
            // this ensures IDs stay flat (e.g. abc-duplicate-3) rather than nested (abc-duplicate-1-duplicate-1)
            const baseId = stripClientDuplicateSuffix(action.payload?.baseBookingId || cleanBooking._id || cleanBooking.id);
            if (!baseId) return;
            const normalizedFlightKey = normalizeClientDuplicateFlightKey(cleanBooking, flightKey, baseId);
            const existingBookings = [
                ...(state.bookingsByFlight[normalizedFlightKey] || []),
                ...(normalizedFlightKey === flightKey ? [] : (state.bookingsByFlight[flightKey] || [])),
            ];
            const storedDuplicateIds = state.clientDuplicates
                .map(dup => dup?.booking?._id)
                .filter(id => id?.startsWith(`${baseId}-duplicate-`));

            // Collect ALL known duplicate IDs from both sources (bookingsByFlight + assigned tasks)
            const allDuplicateIds = [
                ...existingBookings
                    .filter(b => b._id?.startsWith(`${baseId}-duplicate-`))
                    .map(b => b._id),
                ...storedDuplicateIds,
                ...assignedDuplicateIds
            ];

            // Find the highest existing duplicate number across all known duplicates
            let maxNumber = 0;
            for (const id of allDuplicateIds) {
                const num = parseInt(id.split("-duplicate-")[1], 10);
                if (!isNaN(num) && num > maxNumber) {
                    maxNumber = num;
                }
            }
            const nextNumber = maxNumber + 1;

            // Get original name (handle different booking structures)
            let originalName = cleanBooking.name || cleanBooking.flightData?.name || '';

            // For ramp/boarding bookings with passengers array, get the first passenger's name
            if (cleanBooking.passengers && cleanBooking.passengers.length > 0) {
                const firstPassenger = cleanBooking.passengers.find(p => p.bookingType !== "DUPLICATE");
                if (firstPassenger) {
                    originalName = firstPassenger.name;
                }
            }

            const duplicateId = `${baseId}-duplicate-${nextNumber}`;

            const duplicatedBooking = {
                ...cleanBooking,
                _id: duplicateId,
                id: duplicateId,
                name: `${originalName} - PART ${nextNumber}`, // Name and ID now use the same number
                isClientDuplicate: true, // Flag to identify client-side duplicates
                clientDuplicateNumber: nextNumber,
                originalBoardingBookingId: baseId,
            };

            console.log('duplicatedBooking ', duplicatedBooking);

            // Add to bookings list
            if (!state.bookingsByFlight[normalizedFlightKey]) {
                state.bookingsByFlight[normalizedFlightKey] = [];
            }

            state.bookingsByFlight[normalizedFlightKey].push(duplicatedBooking);
            // Persist to clientDuplicates so they survive data refreshes
            state.clientDuplicates.push({ booking: duplicatedBooking, flightKey: normalizedFlightKey });
        },
        resetManualUpdateBookingsTimes: (state) => {
            state.manualUpdateBookingsTimesStatus = 'idle';
            state.manualUpdateBookingsTimesError = null;
        },

        resetDeleteBookingNote: (state) => {
            state.deleteBookingNoteStatus = 'idle';
            state.deleteBookingNoteError = null;
        },
    },
    extraReducers: {
        [getBookings.pending]: (state, action) => {
            state.status = "loading";
            state.error = null;
        },
        [getBookings.fulfilled]: (state, action) => {
            state.status = "succeeded";
            state.bookingsByFlight = action.payload || {};
            state.queryTime = new Date(); // Cache query time

            // Re-inject client-side duplicates that were created locally
            // (these only live in Redux and would otherwise be lost on data refresh)
            for (const dup of state.clientDuplicates) {
                const { booking } = dup;
                const flightKey = normalizeClientDuplicateFlightKey(
                    booking,
                    dup.flightKey,
                    booking?.originalBoardingBookingId
                );
                if (!booking || !flightKey) continue;
                dup.flightKey = flightKey;

                if (!state.bookingsByFlight[flightKey]) {
                    state.bookingsByFlight[flightKey] = [];
                }
                // Only re-add if not already present (e.g. if the server now returns this ID)
                const alreadyExists = state.bookingsByFlight[flightKey].some(b => b._id === booking._id);
                if (!alreadyExists) {
                    state.bookingsByFlight[flightKey].push(booking);
                }
            }
        },
        [getBookings.rejected]: (state, action) => {
            state.status = "failed";
            state.bookingsByFlight = {};
            state.error = action.error.message;
        },
        [postBookingList.pending]: (state, action) => {
            state.postStatus = "loading";
            state.postError = null;
        },
        [postBookingList.fulfilled]: (state, action) => {

            state.postStatus = "succeeded";

            // check payload
            const newBookings = Array.isArray(action.payload?.response)
                ? action.payload.response
                : [];
            // for each booking, add to appropriate flight bucket
            for (let booking of newBookings) {

                // get booking details
                const fc = booking.flightConfirmationDetails || booking.flightDetails;

                // adjust id
                const normalizedId = booking._id || booking.id || booking.bookingId;
                booking._id = normalizedId;

                // create flight id
                const key = `${fc.carrierCode}${fc.flightNumber}_${fc.origin}_${fc.destination}_${fc.departureDate}`;

                // ensure flight key exists
                if (!state.bookingsByFlight[key]) {
                    state.bookingsByFlight[key] = [];
                }

                // prevent duplicates by _id
                const exists = state.bookingsByFlight[key].some(b => b._id === booking._id);

                if (exists) {
                    // skip duplicates
                }
                else {
                    // check if arrival format the booking to be the timeline arrival format
                    if (booking?.iata === booking?.flightConfirmationDetails?.destination) {
                        booking = createArrivalDoc(booking, booking?.iata, state.bookingsByFlight[key]);
                    }
                    state.bookingsByFlight[key].push(booking);
                }
            }
        },
        [postBookingList.rejected]: (state, action) => {
            state.postStatus = "failed";
            state.postBookings = {};
            state.postError = action.error.message;
        },
        // addFlightPassenger reducers (dedicated for timeline-v2 flight-information)
        [addFlightPassenger.pending]: (state, action) => {
            state.addFlightPassengerStatus = "loading";
            state.addFlightPassengerError = null;
        },
        [addFlightPassenger.fulfilled]: (state, action) => {

            state.addFlightPassengerStatus = "succeeded";

            // check payload
            const newBookings = Array.isArray(action.payload?.response)
                ? action.payload.response
                : [];
            // for each booking, add to appropriate flight bucket
            for (let booking of newBookings) {

                // get booking details
                const fc = booking.flightConfirmationDetails || booking.flightDetails;

                // adjust id
                const normalizedId = booking._id || booking.id || booking.bookingId;
                booking._id = normalizedId;

                // create flight id
                const key = `${fc.carrierCode}${fc.flightNumber}_${fc.origin}_${fc.destination}_${fc.departureDate}`;

                // ensure flight key exists
                if (!state.bookingsByFlight[key]) {
                    state.bookingsByFlight[key] = [];
                }

                // prevent duplicates by _id
                const exists = state.bookingsByFlight[key].some(b => b._id === booking._id);

                if (exists) {
                    // skip duplicates
                }
                else {
                    // check if arrival format the booking to be the timeline arrival format
                    if (booking?.iata === booking?.flightConfirmationDetails?.destination) {
                        booking = createArrivalDoc(booking, booking?.iata, state.bookingsByFlight[key]);
                    }
                    state.bookingsByFlight[key].push(booking);
                }
            }
            state.addFlightPassengerBookings = newBookings;
        },
        [addFlightPassenger.rejected]: (state, action) => {
            // Check if it's a 422 error (departure date required)
            if (action.error.message === "DEPARTURE_DATE_REQUIRED") {
                state.addFlightPassengerStatus = "needs_departure_date";
                state.addFlightPassengerNeedsDepartureDate = true;
                state.addFlightPassengerError = null;
            } else {
                state.addFlightPassengerStatus = "failed";
                state.addFlightPassengerBookings = [];
                state.addFlightPassengerError = action.error.message;
            }
        },
        [duplicateTask.pending]: (state) => {
            state.duplicateStatus = "loading";
            state.duplicateError = null;
        },
        [duplicateTask.fulfilled]: (state, action) => {
            state.duplicateStatus = "succeeded";
            state.duplicatedTask = action.payload;

            // Extract the duplicate booking from the response
            const duplicateBooking = action.payload.booking;
            const originalBooking = action.payload.originalBooking;

            if (duplicateBooking) {
                const fc = duplicateBooking.flightConfirmationDetails || duplicateBooking.flightDetails;

                if (fc) {
                    const key = `${fc.carrierCode}${fc.flightNumber}_${fc.origin}_${fc.destination}_${fc.departureDate}`;

                    // Ensure flight key exists
                    if (!state.bookingsByFlight[key]) {
                        state.bookingsByFlight[key] = [];
                    }

                    // Append part number to the name if it's a duplicate
                    const bookingWithUpdatedName = {
                        ...duplicateBooking,
                        name: duplicateBooking.partNumber
                            ? `${duplicateBooking.name} - PART ${duplicateBooking.partNumber}`
                            : duplicateBooking.name
                    };

                    // Check if arrival - format the booking to be the timeline arrival format
                    let formattedBooking = bookingWithUpdatedName;
                    if (bookingWithUpdatedName?.iata === bookingWithUpdatedName?.flightConfirmationDetails?.destination) {
                        formattedBooking = createArrivalDoc(bookingWithUpdatedName, bookingWithUpdatedName?.iata, state.bookingsByFlight[key]);
                    }

                    // Add duplicate to bookings list
                    state.bookingsByFlight[key].push(formattedBooking);

                    // Update the original booking in the list with the updated duplicateBookings array
                    if (originalBooking) {
                        const originalIndex = state.bookingsByFlight[key].findIndex(
                            b => b._id === originalBooking._id || b._id === originalBooking.id
                        );

                        if (originalIndex !== -1) {
                            // Update the original booking with the new duplicateBookings array
                            const updatedOriginal = { ...state.bookingsByFlight[key][originalIndex] };

                            // If it's an arrival doc, update the nested originalBookingDoc
                            if (updatedOriginal.originalBookingDoc) {
                                updatedOriginal.originalBookingDoc = {
                                    ...updatedOriginal.originalBookingDoc,
                                    duplicateBookings: originalBooking.duplicateBookings
                                };
                            } else {
                                updatedOriginal.duplicateBookings = originalBooking.duplicateBookings;
                            }

                            state.bookingsByFlight[key][originalIndex] = updatedOriginal;
                        }
                    }
                }
            }
        },
        [duplicateTask.rejected]: (state, action) => {
            state.duplicateStatus = "failed";
            state.duplicatedTask = null;
            state.duplicateError = action.error.message;
        },

        // Update booking position info when setBookingLocation succeeds (checked-in, etc.)
        [setBookingLocation.fulfilled]: (state, action) => {
            updateBookingInState(state, action.payload, {});
        },

        // Update booking position info when paxCheckOut succeeds
        [paxCheckOut.fulfilled]: (state, action) => {
            updateBookingInState(state, action.payload, {});
        },

        // Update entire booking when updateBooking succeeds
        [updateBooking.fulfilled]: (state, action) => {
            updateBookingInState(state, action.payload, { mergeAll: true });
        },

        [arrivalPaxCheckIn.fulfilled]: (state, action) => {
            updateBookingInState(state, action?.payload?.booking ?? null, { mergeAll: true });
        },
        [unRejectPax.fulfilled]: (state, action) => {
            updateBookingInState(state, action?.payload?.bookingDoc, { mergeAll: true });
        },

        // updateTimelineBooking - dedicated thunk for timeline-v2
        [updateTimelineBooking.pending]: (state) => {
            state.updateStatus = 'loading';
            state.updateError = null;
        },
        [updateTimelineBooking.fulfilled]: (state, action) => {
            state.updateStatus = 'succeeded';
            updateBookingInState(state, action.payload, { mergeAll: true });
        },
        [updateTimelineBooking.rejected]: (state, action) => {
            state.updateStatus = 'failed';
            state.updateError = action.error.message;
        },
        [deleteTimelineJob.fulfilled]: (state, action) => {
            updateBookingInState(state, action.payload.jobId, {});
        },


        // updateALTPickupTime - for updating pickup time on ALT pickup tasks
        [updateALTPickupTime.pending]: (state) => {
            state.updateALTPickupStatus = 'loading';
            state.updateALTPickupError = null;
        },
        [updateALTPickupTime.fulfilled]: (state, action) => {
            state.updateALTPickupStatus = 'succeeded';
            // Update the task in bookingsByFlight if it exists there
            // ALT pickup tasks have flightInformation instead of flightConfirmationDetails
            const updatedTask = action.payload;
        },
        [updateALTPickupTime.rejected]: (state, action) => {
            state.updateALTPickupStatus = 'failed';
            state.updateALTPickupError = action.error.message;
        },

        // manual update bookings times
        [manualUpdateBookingsTimes.pending]: (state) => {
            state.manualUpdateBookingsTimesStatus = 'loading';
            state.manualUpdateBookingsTimesError = null;
        },
        [manualUpdateBookingsTimes.fulfilled]: (state, action) => {
            state.manualUpdateBookingsTimesStatus = 'succeeded';
        },
        [manualUpdateBookingsTimes.rejected]: (state, action) => {
            state.manualUpdateBookingsTimesStatus = 'failed';
            state.manualUpdateBookingsTimesError = action.error.message;
        },

        // delete booking note
        [deleteBookingNote.pending]: (state) => {
            state.deleteBookingNoteStatus = 'loading';
            state.deleteBookingNoteError = null;
        },
        [deleteBookingNote.fulfilled]: (state, action) => {
            state.deleteBookingNoteStatus = 'succeeded';
        },
        [deleteBookingNote.rejected]: (state, action) => {
            state.deleteBookingNoteStatus = 'failed';
            state.deleteBookingNoteError = action.error.message;
        },
    }
});

export default timelineBookingsSlice.reducer;

// Selectors
export const selectStatus = state => state.timelineBookings.status;
export const selectError = state => state.timelineBookings.error;
export const selectQueryTime = state => state.timelineBookings.queryTime;

// post selectors
export const selectPostedBookings = state => state.timelineBookings.postBookings;
export const selectPostedBookingsStatus = state => state.timelineBookings.postStatus;
export const selectPostedBookingsError = state => state.timelineBookings.postError;

// addFlightPassenger selectors (dedicated for timeline-v2 flight-information)
export const selectAddFlightPassengerBookings = state => state.timelineBookings.addFlightPassengerBookings;
export const selectAddFlightPassengerStatus = state => state.timelineBookings.addFlightPassengerStatus;
export const selectAddFlightPassengerError = state => state.timelineBookings.addFlightPassengerError;
export const selectAddFlightPassengerNeedsDepartureDate = state => state.timelineBookings.addFlightPassengerNeedsDepartureDate;
export const selectAddFlightPassengerPendingBooking = state => state.timelineBookings.addFlightPassengerPendingBooking;

/**
 * OPTIMIZED: Select bookings for a specific flight
 * This selector only causes re-renders when the specific flight's bookings change
 * 
 * @param {string} flightNumber - Flight number (e.g., "EI101", "BA825")
 * @returns {Function} Selector function that returns bookings for the specified flight
 * 
 * @example
 * const bookingsForEI101 = useSelector(selectBookingsByFlight("EI101"));
 */
//export const selectBookingsByFlight = (flightNumber) => (state) => {
//  return state.timelineBookings.bookingsByFlight[flightNumber] || [];
//};
export const selectAllBookingsByFlight = (flightNumber) => (state) => {
    return state.timelineBookings.bookingsByFlight[flightNumber] || [];
};

/**
 * Overnight-arrival reconciliation.
 *
 * A booking's flight bucket key and a flight row's `bookingKey` both embed a date
 * (`<carrier><number>_<origin>_<destination>_<YYYY-MM-DD>`). For arrivals that cross
 * midnight, the booking date is derived from the booking's own `arrivalTime` (server:
 * prm-bookings formatFlightDictionary) while the flight row's date comes from the flight
 * feed's operation time (server: prm-flights timeline-utils). When a SITA arrival carries a
 * pre-midnight scheduled time but the flight actually lands just after midnight, the two land
 * in adjacent date buckets — so the booking never renders under the flight row and has no
 * per-passenger "Assign" option (only a client-added duplicate, keyed off the flight, shows).
 *
 * This selector heals that: any bucket with bookings but no matching flight row ("orphan") is
 * attached to the single nearest real flight row that shares the same
 * carrier/number/origin/destination within ±1 day. Boarding combos and buckets that already
 * own a flight row are left alone, so a normal daily service (each day claimed by its own row)
 * is never touched.
 */
const parseFlightBucketKey = (key) => {
    if (typeof key !== 'string') return null;
    const idx = key.lastIndexOf('_');
    if (idx === -1) return null;
    const date = key.slice(idx + 1);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
    return { prefix: key.slice(0, idx), date };
};

const bucketDayGap = (dateA, dateB) => {
    const a = Date.parse(`${dateA}T00:00:00Z`);
    const b = Date.parse(`${dateB}T00:00:00Z`);
    if (Number.isNaN(a) || Number.isNaN(b)) return Infinity;
    return Math.abs(a - b) / 86400000;
};

const MAX_BUCKET_DAY_GAP = 1;

// Map of realFlightBookingKey -> [orphanBucketKey, ...] to merge into that flight row.
export const selectOrphanBucketAttachments = createSelector(
    [
        (state) => state.timelineBookings.bookingsByFlight,
        selectFlightsByBookingKey,
    ],
    (bookingsDict, flightsByKey) => {
        const attachments = {};
        if (!bookingsDict) return attachments;

        // Group real flight bucket keys by their carrier/number/origin/destination prefix
        const realKeysByPrefix = {};
        for (const realKey in flightsByKey) {
            if (realKey.endsWith('_BOARDING')) continue;
            const parsed = parseFlightBucketKey(realKey);
            if (!parsed) continue;
            if (!realKeysByPrefix[parsed.prefix]) realKeysByPrefix[parsed.prefix] = [];
            realKeysByPrefix[parsed.prefix].push({ key: realKey, date: parsed.date });
        }

        for (const bucketKey in bookingsDict) {
            // Skip buckets that already have their own flight row, boarding combos, and empties
            if (flightsByKey[bucketKey]) continue;
            if (bucketKey.endsWith('_BOARDING')) continue;
            const bookings = bookingsDict[bucketKey];
            if (!Array.isArray(bookings) || bookings.length === 0) continue;

            const parsed = parseFlightBucketKey(bucketKey);
            if (!parsed) continue;

            const candidates = realKeysByPrefix[parsed.prefix];
            if (!candidates || candidates.length === 0) continue;

            // Attach to the single nearest flight row within the day gap.
            // Deterministic tiebreak (earliest date, then key) so one orphan maps to one flight.
            let best = null;
            for (const cand of candidates) {
                const gap = bucketDayGap(parsed.date, cand.date);
                if (gap > MAX_BUCKET_DAY_GAP) continue;
                if (
                    !best ||
                    gap < best.gap ||
                    (gap === best.gap && cand.date < best.date) ||
                    (gap === best.gap && cand.date === best.date && cand.key < best.key)
                ) {
                    best = { key: cand.key, date: cand.date, gap };
                }
            }
            if (!best) continue;
            if (!attachments[best.key]) attachments[best.key] = [];
            attachments[best.key].push(bucketKey);
        }

        return attachments;
    }
);

/* NEW BOOKINGS BY FLIGHT WITH FILTERING */
export const selectBookingsByFlight = (flightNumber) =>
    createSelector(
        [
            state => state.timelineBookings.bookingsByFlight,
            state => state.timelineBookings.taskIds,   // stored as array or Set
            selectOrphanBucketAttachments,
        ],
        (bookingsDict, taskIds, attachments) => {
            const ownBookings = bookingsDict[flightNumber] || [];
            const orphanKeys = attachments[flightNumber];

            // Merge any orphan (adjacent-date) buckets that belong to this flight row
            let bookings = ownBookings;
            if (orphanKeys && orphanKeys.length > 0) {
                bookings = ownBookings.slice();
                for (const key of orphanKeys) {
                    const arr = bookingsDict[key];
                    if (Array.isArray(arr) && arr.length > 0) bookings = bookings.concat(arr);
                }
            }

            if (!taskIds || taskIds.length === 0) return bookings;

            const blocked = new Set(taskIds);

            return bookings.filter(b => {

                if (b?._id?.startsWith("CONNECT-") || b?.isConnectBooking) {
                    return false;
                }

                // Check if this specific booking ID is blocked
                if (blocked.has(b?._id)) {
                    return false;
                }

                // For duplicate bookings (backend or client-side), only filter by exact ID
                // Client duplicates use originalBoardingBookingId (not originalBookingId) so they won't be caught below
                if (b?.bookingType === "DUPLICATE" || b?.isClientDuplicate === true) {
                    return true;
                }

                // For non-duplicate bookings, also check originalBookingId
                // This handles arrival bookings where originalBookingId links back to the departure doc
                if (blocked.has(b?.originalBookingId)) {
                    return false;
                }

                return true;
            });

        }
    );

/* OLD SELECT BOOKINGS BY FLIGHT WITHOUT FILTERING!
export const selectBookingsByFlight = (flightNumber) => (state) => {
  return state.timelineBookings.bookingsByFlight[flightNumber] || [];
};
*/


/**
 * Select all bookings grouped by flight
 */
export const selectBookingsByFlightDict = state => {
    return state.timelineBookings.bookingsByFlight;
};

/**
 * Select all flight numbers that have bookings
 */
export const selectFlightsWithBookings = state => {
    return Object.keys(state.timelineBookings.bookingsByFlight).sort();
};

/**
 * Select all bookings as a flat array
 */
export const selectBookingsArray = state => {
    return Object.values(state.timelineBookings.bookingsByFlight).flat();
};

/**
 * Select total booking count
 */
export const selectTotalBookingCount = state => {
    return Object.values(state.timelineBookings.bookingsByFlight).reduce(
        (total, bookings) => total + (bookings?.length || 0),
        0
    );
};

// task ids
export const selectTaskIds = state => {
    return state.timelineBookings.taskIds;
}

// check if task id exists
export const selectHasTaskId = (taskId) => (state) => {
    const taskIds = state.timelineBookings.taskIds;
    if (!taskIds || taskIds.length === 0) return false;
    return taskIds.includes(taskId);
};
// check if task id exists
export const selectHasRampFlightId = (taskId) => (state) => {
    const taskIds = state.timelineBookings.taskIds;
    if (!taskIds || taskIds.length === 0) return false;
    return taskIds.includes(taskId);
};
// duplicate task selectors
export const selectDuplicateStatus = state => state.timelineBookings.duplicateStatus;
export const selectDuplicatedTask = state => state.timelineBookings.duplicatedTask;
export const selectDuplicateError = state => state.timelineBookings.duplicateError;

// update booking selectors (for timeline-v2)
export const selectUpdateStatus = state => state.timelineBookings.updateStatus;
export const selectUpdateError = state => state.timelineBookings.updateError;

// update ALT pickup selectors
export const selectUpdateALTPickupStatus = state => state.timelineBookings.updateALTPickupStatus;
export const selectUpdateALTPickupError = state => state.timelineBookings.updateALTPickupError;

// manual update bookings times selectors
export const selectManualUpdateBookingsTimesStatus = state => state.timelineBookings.manualUpdateBookingsTimesStatus;
export const selectManualUpdateBookingsTimesError = state => state.timelineBookings.manualUpdateBookingsTimesError;

// ramp flight ids selectors
export const selectRampFlightIds = state => state.timelineBookings.rampFlightIds;
export const selectRampMarshallFlightIds = state => state.timelineBookings.rampMarshallFlightIds;

// delete booking note selectors
export const selectDeleteBookingNoteStatus = state => state.timelineBookings.deleteBookingNoteStatus;
export const selectDeleteBookingNoteError = state => state.timelineBookings.deleteBookingNoteError;
// Export actions
export const { reset, setQueryTime, setTaskIds, resetPostBookings, resetAddFlightPassenger, setAddFlightPassengerNeedsDepartureDate, resetDuplicateTask, resetUpdateBooking, resetUpdateALTPickup, duplicateBookingLocally, removeClientDuplicate, deleteClientDuplicate, resetManualUpdateBookingsTimes, setRampFlightIds, setRampMarshallFlightIds, resetDeleteBookingNote } = timelineBookingsSlice.actions;
